import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useI18n } from '../../i18n';
import { Colors, Radius, Shadow, Spacing, Typography } from '../../theme';
import { CAPTURE_CONFIRM_DELAY_MS } from './poseVerificationConfig';
import { useVibeCameraSession } from './useVibeCameraSession';
import { useVibeVerificationStateMachine } from './useVibeVerificationStateMachine';
import type { FacePose } from './types';

// Web counterpart to VibeVerificationCameraModal.tsx — identical flow, copy,
// and UI (reuses the exact same useVibeVerificationStateMachine.ts and
// poseVerificationConfig.ts as native, unmodified), swapping VisionCamera's
// <Camera> for a mirrored <video> element backed by getUserMedia() (see
// useVibeCameraSession.web.ts) and ML Kit's native module for
// nativeVibeVerification.web.ts's MediaPipe FaceLandmarker.

export type VibeVerificationCaptureResult = {
  frontPath: string;
  leftPath: string;
  rightPath: string;
  // See the identical field's comment on the native VibeVerificationCameraModal.tsx —
  // true only for the manual-review fallback, which must never be routed to
  // automated self-approval.
  isFallback: boolean;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onCaptured: (result: VibeVerificationCaptureResult) => void;
};

const POSE_ORDER: FacePose[] = ['front', 'left', 'right'];

const VibeVerificationCameraModal: React.FC<Props> = ({ visible, onClose, onCaptured }) => {
  const { t } = useI18n();
  const insets = useSafeAreaInsets();
  const machine = useVibeVerificationStateMachine();
  const { state } = machine;
  const captureTriggeredRef = useRef(false);
  const capturesRef = useRef<Partial<Record<FacePose, string>>>({});

  const captureBadgeAnim = useRef(new Animated.Value(0)).current;
  const [capturedPoseLabel, setCapturedPoseLabel] = useState<FacePose | null>(null);

  const cameraActive = visible && state.phase !== 'captured';
  const pollingActive = cameraActive && state.phase !== 'poseConfirmed' && state.phase !== 'capturing';
  const { videoRef, permissionState, captureFinalPhoto } = useVibeCameraSession(
    cameraActive,
    pollingActive,
    machine.processAnalysis,
  );

  useEffect(() => {
    if (!visible) {
      captureTriggeredRef.current = false;
      capturesRef.current = {};
      machine.reset();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (permissionState === 'denied') {
      machine.setPermissionDenied();
    } else if (permissionState === 'granted' && state.phase === 'requestingPermission') {
      machine.startLiveCamera();
    }
  }, [permissionState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.phase !== 'poseConfirmed' || captureTriggeredRef.current) return;
    captureTriggeredRef.current = true;
    const pose = state.currentPose;
    const timer = setTimeout(async () => {
      machine.markCapturing();
      try {
        const path = await captureFinalPhoto();
        capturesRef.current[pose] = path;
        captureTriggeredRef.current = false;
        machine.advancePose();
      } catch (err) {
        if (__DEV__) console.log('[VibePoseDebug][Web] capture FAILED, surfacing error state', { pose, err });
        captureTriggeredRef.current = false;
        machine.setError('capture_failure');
      }
    }, CAPTURE_CONFIRM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [state.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.phase !== 'captured') return;
    const { front, left, right } = capturesRef.current;
    if (front && left && right) {
      onCaptured({ frontPath: front, leftPath: left, rightPath: right, isFallback: false });
    } else {
      machine.setError('unknown');
    }
  }, [state.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const show = state.phase === 'poseConfirmed' || state.phase === 'capturing';
    if (show) setCapturedPoseLabel(state.currentPose);
    Animated.timing(captureBadgeAnim, {
      toValue: show ? 1 : 0,
      duration: show ? 220 : 180,
      useNativeDriver: true,
    }).start();
  }, [state.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFallbackSubmit = async () => {
    try {
      const path = await captureFinalPhoto();
      capturesRef.current[state.currentPose] = path;
      const { front, left, right } = capturesRef.current;
      onCaptured({ frontPath: front || path, leftPath: left || path, rightPath: right || path, isFallback: true });
    } catch {
      machine.setError('capture_failure');
    }
  };

  const poseIndex = POSE_ORDER.indexOf(state.currentPose);

  const instructionText = useMemo(() => {
    switch (state.phase) {
      case 'requestingPermission':
        return t('verification.camera.requestingPermission');
      case 'permissionDenied':
        return t('verification.camera.permissionDenied');
      case 'front':
        if (state.faceCount > 1) return t('verification.camera.onePersonOnly');
        if (!state.faceDetected) return t('verification.camera.moveIntoFrame');
        return t('verification.camera.lookStraight');
      case 'left':
        if (state.faceCount > 1) return t('verification.camera.onePersonOnly');
        if (!state.faceDetected) return t('verification.camera.moveIntoFrame');
        return t('verification.camera.turnLeft');
      case 'right':
        if (state.faceCount > 1) return t('verification.camera.onePersonOnly');
        if (!state.faceDetected) return t('verification.camera.moveIntoFrame');
        return t('verification.camera.turnRight');
      case 'poseConfirmed':
      case 'capturing':
        return t('verification.camera.holdStill');
      case 'fallback':
        return t('verification.camera.fallbackMessage');
      case 'error':
        return t('verification.camera.errorGeneric');
      default:
        return '';
    }
  }, [state.phase, state.faceCount, state.faceDetected, t]);

  const isPoseStep = state.phase === 'front' || state.phase === 'left' || state.phase === 'right'
    || state.phase === 'poseConfirmed' || state.phase === 'capturing';

  const capturedLabelText = capturedPoseLabel === 'front' ? t('verification.camera.frontCaptured')
    : capturedPoseLabel === 'left' ? t('verification.camera.leftCaptured')
      : capturedPoseLabel === 'right' ? t('verification.camera.rightCaptured')
        : '';

  const hasBottomActions = state.phase === 'permissionDenied' || state.phase === 'error' || state.phase === 'fallback';

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.container}>
        {cameraActive && permissionState === 'granted' ? (
          // Raw DOM element (same pattern as TonyteMapView.web.tsx's
          // <iframe>) — mirrored via CSS so the preview reads naturally as a
          // selfie camera; the underlying frame data fed to analysis/capture
          // is NOT mirrored (see useVibeCameraSession.web.ts), matching how
          // a phone's own front camera preview is mirrored while its saved
          // photos are not.
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
            }}
          />
        ) : (
          <View style={[StyleSheet.absoluteFill, styles.cameraPlaceholder]}>
            <ActivityIndicator color={Colors.turquoise} />
          </View>
        )}

        <View style={[styles.topBar, { paddingTop: insets.top + Spacing.sm }]}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Icon name="x" size={22} color="#fff" />
          </TouchableOpacity>
          {isPoseStep ? (
            <View style={styles.stepDots}>
              {POSE_ORDER.map((pose, index) => (
                <View
                  key={pose}
                  style={[
                    styles.stepDot,
                    index === poseIndex && styles.stepDotActive,
                    index < poseIndex && styles.stepDotDone,
                  ]}
                />
              ))}
            </View>
          ) : null}
        </View>

        {instructionText ? (
          <View style={[styles.instructionBannerWrap, { top: insets.top + Spacing.sm + 56 }]} pointerEvents="none">
            <View style={styles.instructionBanner}>
              <Text style={styles.instructionText}>{instructionText}</Text>
            </View>
          </View>
        ) : null}

        <Animated.View
          pointerEvents="none"
          style={[
            styles.captureBadgeWrap,
            {
              opacity: captureBadgeAnim,
              transform: [{ scale: captureBadgeAnim.interpolate({ inputRange: [0, 1], outputRange: [0.8, 1] }) }],
            },
          ]}
        >
          <View style={styles.captureBadge}>
            <Icon name="check-circle" size={36} color={Colors.turquoise} />
            <Text style={styles.captureBadgeText}>{capturedLabelText}</Text>
          </View>
        </Animated.View>

        {hasBottomActions ? (
          <View style={[styles.bottomPanel, { paddingBottom: insets.bottom + Spacing.lg }]}>
            {state.phase === 'permissionDenied' ? (
              <TouchableOpacity style={styles.primaryButton} onPress={() => machine.reset()}>
                <Text style={styles.primaryButtonText}>{t('verification.camera.tryAgain')}</Text>
              </TouchableOpacity>
            ) : null}

            {state.phase === 'error' ? (
              <TouchableOpacity style={styles.primaryButton} onPress={() => machine.reset()}>
                <Text style={styles.primaryButtonText}>{t('verification.camera.tryAgain')}</Text>
              </TouchableOpacity>
            ) : null}

            {state.phase === 'fallback' ? (
              <View style={styles.buttonRow}>
                <TouchableOpacity style={styles.secondaryButton} onPress={() => machine.reset()}>
                  <Text style={styles.secondaryButtonText}>{t('verification.camera.tryAgain')}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.primaryButton} onPress={handleFallbackSubmit}>
                  <Text style={styles.primaryButtonText}>{t('verification.camera.fallbackSubmit')}</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#000' },
  cameraPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' },
  topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: Spacing.base },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  stepDots: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: 'rgba(255,255,255,0.28)',
  },
  stepDotActive: {
    width: 26,
    backgroundColor: Colors.turquoise,
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.55,
    shadowRadius: 8,
    elevation: 4,
  },
  stepDotDone: { backgroundColor: 'rgba(38,208,206,0.65)' },
  instructionBannerWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
  },
  instructionBanner: {
    maxWidth: 440,
    backgroundColor: 'rgba(4,8,15,0.88)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: Radius.xl,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    ...Shadow.card,
  },
  instructionText: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    lineHeight: 24,
    textAlign: 'center',
  },
  captureBadgeWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  captureBadge: {
    alignItems: 'center',
    gap: Spacing.sm,
    backgroundColor: 'rgba(4,8,15,0.94)',
    borderWidth: 1.5,
    borderColor: Colors.turquoise,
    borderRadius: Radius.xxl,
    paddingHorizontal: Spacing.xxl,
    paddingVertical: Spacing.xl,
    ...Shadow.glow,
    shadowColor: Colors.turquoise,
  },
  captureBadgeText: {
    color: Colors.turquoise,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  bottomPanel: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.lg,
    gap: Spacing.sm,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  buttonRow: { flexDirection: 'row', gap: Spacing.sm },
  primaryButton: {
    minHeight: 48,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.turquoise,
    paddingHorizontal: Spacing.base,
    flex: 1,
  },
  primaryButtonText: { color: '#07101d', fontWeight: Typography.bold, fontSize: Typography.base },
  secondaryButton: {
    flex: 1,
    minHeight: 48,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  secondaryButtonText: { color: '#fff', fontWeight: Typography.bold, fontSize: Typography.base },
});

export default VibeVerificationCameraModal;
